import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Button } from '@mui/material';
import StudentLayout from '../../components/student/StudentLayout';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useRequestLession } from '../../hooks/useRequestLession';
import Loading from '../../components/Loading';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

// ✅ مكون عرض معلومة واحدة
const InfoBox = ({ label, value }) => (
  <Box className='border px-3 py-2 rounded w-52 text-center'>
    <Typography variant="body1" fontWeight="bold">{label}:</Typography>
    <Typography variant="h6">{value}</Typography>
  </Box>
);

// ✅ كرت الدرس
const LessonCard = ({ item, t, onPay, onDelete }) => {
  return (
    <Box sx={{ marginBottom: "12px", background: "#f2f3f4", padding: "1rem" }}>
      <Box sx={{ marginBottom: "8px" }}>
        <Typography variant="h6" className='flex justify-end items-center'>
          {item.teacher?.firstName} {item.teacher?.lastName}
          <img
            src={item.teacher?.image ? `${process.env.REACT_APP_API_KEY}images/${item.teacher?.image}` : "/logo.png"}
            alt="teacher"
            style={{ borderRadius: "50%", height: "80px", width: "80px", marginRight: "1rem", marginBottom: "1rem" }}
          />
        </Typography>

        <Box className="flex justify-around">
          <InfoBox label={t("Time")} value={dayjs(item.date).format('HH:mm')} />
          <InfoBox label={t("day")} value={dayjs(item.date).format('YYYY-MM-DD')} />
        </Box>

        <Box className="flex justify-around">
          <InfoBox
            label={t("status")}
            value={
              item?.status === 'pending' ? <span className='text-[#f4d03f]'>{t('pending')}</span> :
              item?.status === 'approved' ? <span className='text-[#2ecc71]'>{t('approved')}</span> :
              <span className='text-[#e74c3c]'>{t('canceled')}</span>
            }
          />
          <InfoBox label={t("lessonType")} value={t(item?.type)} />
        </Box>

        <Box className="flex justify-around">
          <InfoBox label={t("Session number")} value={item?.period} />
          <InfoBox label={t("price")} value={`${item?.price} ${t(item?.currency)}`} />
        </Box>
      </Box>

      <Box className="flex justify-around mt-5">
        {item?.status === "approved" && (
          item?.paymentLink ? (
            <Button variant="contained" color="primary" onClick={() => onPay(item?.id)}>
              {t("You can now pay your reservation")}
            </Button>
          ) : (
            <div className='text-[#e74c3c] my-3'>
              {t("Sorry, I was late for the payment. You can book again.")}
            </div>
          )
        )}
        <Button variant="contained" color="error" onClick={() => onDelete(item.id)}>
          {t("delete")}
        </Button>
      </Box>
    </Box>
  );
};

// ✅ المكون الرئيسي
export default function StudentLession() {
  const { t } = useTranslation();
  const { student } = useSelector(state => state.student);
  const { data, isLoading, refetch } = useRequestLession(student?.id); // تأكد من أن hook يدعم refetch
  const navigate = useNavigate();
  const [lessons, setLessons] = useState([]);

  useEffect(() => {
    if (data?.data) {
      setLessons(data.data);
    }
  }, [data]);

  // ✅ تحديث البيانات كل 3 ثواني
  useEffect(() => {
    const interval = setInterval(() => {
      refetch(); // يعيد جلب البيانات من السيرفر
    }, 3000);

    return () => clearInterval(interval); // تنظيف عند إلغاء التركيب
  }, [refetch]);

  const handlePayment = (id) => {
    navigate(`/student/PayLesson/${id}`);
  };

  const handleDelete = async (id) => {
    const previousLessons = [...lessons];
    setLessons(prev => prev.filter(item => item.id !== id));
    try {
      const response = await fetch(`${process.env.REACT_APP_API_KEY}api/v1/lesson/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);
    } catch (error) {
      console.error("Error deleting lesson:", error);
      setLessons(previousLessons);
    }
  };

  return (
    <StudentLayout>
      <Paper sx={{ padding: "20px" }}>
        <Typography sx={{ fontSize: "24px", marginTop: "12px", fontWeight: "600", marginBottom: "30px" }}>
          {t('Lesson booking requests')}
        </Typography>

        {!isLoading ? (
          lessons.length > 0 ? (
            lessons.map(item => (
              <LessonCard key={item.id} item={item} t={t} onPay={handlePayment} onDelete={handleDelete} />
            ))
          ) : (
            <Typography className='text-center'>{t("No requests were found to book lessons.")}</Typography>
          )
        ) : (
          <Loading />
        )}
      </Paper>
    </StudentLayout>
  );
}